/**
 * Schedule picker (Agents v3, D6) — the client-facing replacement for the raw
 * cron field: a cadence (Weekly / Monthly / Daily, as the agent type allows),
 * a day (weekday or day of the month, 1–28 so it exists in every month) and a
 * time of day. It composes a 5-field cron for the existing API; the cron is
 * still what is stored, so nothing on the wire changes.
 *
 * A cron the picker cannot represent (hand-written before this picker, or
 * set through the API) is shown verbatim as "Custom schedule" with a button
 * that swaps in a simple weekly one — never silently rewritten.
 *
 * Controlled: `cron` in, `onChange(cron)` out. The timezone is not editable
 * here (it is the brand's); the host renders it next to the picker.
 */
import { useTranslation } from 'react-i18next';
import type { AgentCadence } from '../../../lib/api/client-reports/client-agents-api';
import {
  DEFAULT_CADENCES,
  composeCronSchedule,
  describeSchedule,
  parseCronSchedule,
  weekdayLabel,
  type CronSchedulePreset,
  type Translate,
} from './agent-helpers';
import '../client-reports.scss';

function cadenceLabel(cadence: AgentCadence, t: Translate): string {
  switch (cadence) {
    case 'daily':
      return t('AgentScheduleCadenceDaily', 'Every day');
    case 'weekly':
      return t('AgentScheduleCadenceWeekly', 'Weekly');
    case 'monthly':
      return t('AgentScheduleCadenceMonthly', 'Monthly');
    default:
      return String(cadence);
  }
}

/** Cron day-of-week values, Monday first. */
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0];

/** 1–28: a day that exists in every month, so a monthly run never skips February. */
const MONTH_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

/** The picker's fallback when a custom cron is reset: Monday 07:00. */
export const SIMPLE_WEEKLY: CronSchedulePreset = {
  cadence: 'weekly',
  day: 1,
  hour: 7,
  minute: 0,
};

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

interface Props {
  cron: string;
  /** Shown in the summary line only — not editable here. */
  timezone: string;
  /** From the registry's `allowed_cadences`; falls back to weekly + monthly. */
  allowedCadences?: AgentCadence[];
  disabled?: boolean;
  /** Prefix for the control ids/labels (one picker per agent type on a page). */
  idPrefix: string;
  onChange: (cron: string) => void;
}

const SchedulePicker = ({
  cron,
  timezone,
  allowedCadences,
  disabled = false,
  idPrefix,
  onChange,
}: Props) => {
  const { t } = useTranslation();
  const cadences =
    Array.isArray(allowedCadences) && allowedCadences.length > 0
      ? allowedCadences
      : DEFAULT_CADENCES;
  const preset = parseCronSchedule(cron);

  const update = (patch: Partial<CronSchedulePreset>) => {
    const base = preset ?? SIMPLE_WEEKLY;
    let next: CronSchedulePreset = { ...base, ...patch };
    // Switching cadence picks a sensible day for the new shape.
    if (patch.cadence && patch.cadence !== base.cadence) {
      next = {
        ...next,
        day:
          patch.cadence === 'weekly' ? 1 : patch.cadence === 'monthly' ? 1 : 0,
      };
    }
    onChange(composeCronSchedule(next));
  };

  if (!preset) {
    return (
      <div className='client-agents__schedule-picker'>
        <p className='client-agents__hint'>
          {t('AgentScheduleCustomLead', 'Custom schedule')} (
          <code>{cron || '—'}</code>){' '}
          {t('AgentScheduleCustomTail', '— set through the API.')}
        </p>
        {!disabled && (
          <button
            type='button'
            className='client-agents__open-link client-agents__linkbtn'
            onClick={() => onChange(composeCronSchedule(SIMPLE_WEEKLY))}
          >
            {t('AgentScheduleUseSimple', 'Use a simple schedule instead →')}
          </button>
        )}
      </div>
    );
  }

  const time = `${pad2(preset.hour)}:${pad2(preset.minute)}`;

  return (
    <div className='client-agents__schedule-picker'>
      <div className='client-agents__field'>
        <span className='client-agents__label'>
          {t('AgentScheduleHowOften', 'How often')}
        </span>
        <div
          className='client-agents__seg'
          role='group'
          aria-label={t('AgentScheduleHowOften', 'How often')}
        >
          {cadences.map((c) => {
            const active = preset.cadence === c;
            return (
              <button
                key={c}
                type='button'
                className={`client-agents__seg-btn${
                  active ? ' client-agents__seg-btn--active' : ''
                }`}
                aria-pressed={active}
                disabled={disabled}
                onClick={() => update({ cadence: c })}
              >
                <span className='client-agents__seg-label'>
                  {cadenceLabel(c, t)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className='client-agents__grid'>
        {preset.cadence !== 'daily' && (
          <div className='client-agents__field'>
            <label className='client-agents__label' htmlFor={`${idPrefix}-day`}>
              {preset.cadence === 'weekly'
                ? t('AgentScheduleWhichDay', 'On which day')
                : t('AgentScheduleWhichDate', 'On which date')}
            </label>
            <select
              id={`${idPrefix}-day`}
              className='client-agents__input'
              value={preset.day}
              disabled={disabled}
              onChange={(e) => update({ day: Number(e.target.value) })}
            >
              {preset.cadence === 'weekly'
                ? WEEKDAYS.map((d) => (
                    <option key={d} value={d}>
                      {weekdayLabel(d, t)}
                    </option>
                  ))
                : MONTH_DAYS.map((d) => (
                    <option key={d} value={d}>
                      {t('AgentScheduleDayN', 'Day {{day}}', { day: d })}
                    </option>
                  ))}
              {preset.cadence === 'monthly' && preset.day > 28 && (
                <option value={preset.day}>
                  {t('AgentScheduleDayN', 'Day {{day}}', { day: preset.day })}
                </option>
              )}
            </select>
          </div>
        )}
        <div className='client-agents__field'>
          <label className='client-agents__label' htmlFor={`${idPrefix}-time`}>
            {t('AgentScheduleWhatTime', 'At what time')}
          </label>
          <input
            id={`${idPrefix}-time`}
            className='client-agents__input'
            type='time'
            value={time}
            step={300}
            disabled={disabled}
            onChange={(e) => {
              const m = /^(\d{2}):(\d{2})$/.exec(e.target.value);
              if (!m) return;
              update({ hour: Number(m[1]), minute: Number(m[2]) });
            }}
          />
        </div>
      </div>

      <p className='client-agents__hint'>
        {describeSchedule(cron, timezone, t)}
      </p>
    </div>
  );
};

export default SchedulePicker;
